import { Vector2 as BABYLON_Vector2, Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Color4 as BABYLON_Color4 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.color.js';
import { Material as BABYLON_Material } from '@inter-ikea-betrakta-babylonjs/core/Materials/material.js';
import { InstancedMesh as BABYLON_InstancedMesh } from '@inter-ikea-betrakta-babylonjs/core/Meshes/instancedMesh.js';
import { Constants as BABYLON_Constants } from '@inter-ikea-betrakta-babylonjs/core/Engines/constants.js';
import { UniversalCamera as BABYLON_UniversalCamera } from '@inter-ikea-betrakta-babylonjs/core/Cameras/universalCamera.js';
import { RenderTargetTexture as BABYLON_RenderTargetTexture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/renderTargetTexture.js';
import { Texture as BABYLON_Texture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/texture.js';
import { PostProcess as BABYLON_PostProcess } from '@inter-ikea-betrakta-babylonjs/core/PostProcesses/postProcess.js';
import { ShaderMaterial as BABYLON_ShaderMaterial } from '@inter-ikea-betrakta-babylonjs/core/Materials/shaderMaterial.js';
import { SphereBuilder as BABYLON_SphereBuilder } from '@inter-ikea-betrakta-babylonjs/core/Meshes/Builders/sphereBuilder.js';
// Side effects
import './shaders/light_baker.vertex.js';
import './shaders/light_baker_arealight.fragment.js';
import './shaders/finalize_lightmap.fragment.js';
export class LightBaker {
    constructor(_viewer, job) {
        this._viewer = _viewer;
        this._sample_nr = 0;
        this._main_render_observer = null;
        this.running = false;
        this._on_stop = null;
        this._material_ready = false;
        this._restore_material_map = new Map();
        this._scene = _viewer.scene;
        this._asset_manager = _viewer.get_asset_manager();
        this._current_job = job;
        this._concurrent_samples = 1;
        // This is broken for even sample counts!
        this._total_samples = 1; // The current AreaLight based algorithm only need 1 sample. This member is left due to legacy reasons and may be used if another Lightbakeing algorithm is used.
        this.bake_objects = job.targets;
        this._uvset = job.uvset;
        this._float_rendering = this._scene.getEngine().getCaps().textureFloatRender;
        this._texture_format = BABYLON_Constants.TEXTURETYPE_UNSIGNED_INT;
        const bake_camera = new BABYLON_UniversalCamera('light_bake_camera', new BABYLON_Vector3(0, 0, 0), this._scene);
        bake_camera.setTarget(BABYLON_Vector3.Zero());
        this._bake_camera = bake_camera;
        this._bake_material = this.setup_bake_material(job.area_lights);
        const target_babylon_meshes = this.get_visible_babylon_meshes(job.targets);
        this._write_rt = this.create_render_target(target_babylon_meshes, this._uvset, job.texture_width, job.texture_height, '1');
        this._read_rt = this.create_render_target(target_babylon_meshes, this._uvset, job.texture_width, job.texture_height, '2');
        this._debug_sphere = BABYLON_SphereBuilder.CreateSphere('light_debug_sphere', { segments: 20, diameter: 0.1 }, this._scene);
        this._debug_sphere.setEnabled(false);
        this._debug_instances = [];
        this._debug_line_instances = [];
    }
    reconfigure(job) {
        this._sample_nr = 0;
        this._concurrent_samples = 1;
        // This is broken for even sample counts!
        // As noted in a comment in the constructor, only one sample is ever needed with the current baking system.
        this._total_samples = 1;
        this.bake_objects = job.targets;
        this._uvset = job.uvset;
        // Uneccesary to recompile the bake material if we can just update its parameters instead.
        if (job.area_lights.length === this._current_job.area_lights.length) {
            this.update_bake_material(this._bake_material, job.area_lights);
        }
        else {
            if (this._bake_material) {
                this._bake_material.dispose();
            }
            this._bake_material = this.setup_bake_material(job.area_lights);
        }
        this._material_ready = false;
        // Note, we need to recreate the RTTs every time becaue they might have been disposed or modified.
        const target_babylon_meshes = this.get_visible_babylon_meshes(job.targets);
        this._write_rt = this.create_render_target(target_babylon_meshes, this._uvset, job.texture_width, job.texture_height, '1');
        this._read_rt = this.create_render_target(target_babylon_meshes, this._uvset, job.texture_width, job.texture_height, '2');
        this._current_job = job;
    }
    async_bake() {
        return new Promise((resolve, reject) => {
            this.running = true;
            this._on_stop = (tex) => {
                if (this._sample_nr === this._total_samples) {
                    resolve(tex);
                }
                else {
                    reject(new Error('LightBaker was stopped before completion'));
                }
            };
            this.create_bake_render_observer();
        });
    }
    frames_needed_for_bake_job() {
        return this._total_samples;
    }
    dispose() {
        if (this.running) {
            this.stop_bake();
        }
        this._bake_material.dispose();
        this._bake_camera.dispose();
        this._debug_instances.forEach(instance => {
            instance.dispose();
        });
        this._debug_line_instances.forEach(line => {
            line.dispose();
        });
        this._debug_sphere.dispose();
    }
    create_bake_render_observer() {
        if (this._main_render_observer !== null) {
            console.warn('Light baking was incorrectly interrupted and started again.');
            this._viewer.get_before_render_scene_setup_observable().remove(this._main_render_observer);
            this._main_render_observer = null;
        }
        // TODO: Register bakers render loop after baking material have compiled.
        this._main_render_observer = this._viewer.get_before_render_scene_setup_observable().add(() => {
            this._scene.incrementRenderId();
            this._write_rt.render(false);
            this._viewer.mark_scene_dirty();
        });
    }
    last_frame() {
        return this._sample_nr === this._total_samples;
    }
    setup_bake_material(lights) {
        const defines = [];
        const uniforms = ['world', 'worldView', 'worldViewProjection', 'view', 'projection', 'previousTexture', 'first_sample_index', 'num_samples_total'];
        const samplers = [];
        defines.push(`#define NUM_AREA_LIGHTS ${lights.length}`);
        if (!this._float_rendering) {
            defines.push('#define PACKEDLIGHTMAP');
        }
        for (let i = 0; i < this._concurrent_samples; i++) {
            defines.push(`#define BAKELIGHT${i}`);
        }
        const bake_material = new BABYLON_ShaderMaterial('bake_material', this._scene, {
            vertex: 'light_baker',
            fragment: 'light_baker_arealight'
        }, {
            attributes: ['position', 'normal', 'uv', 'uv2'],
            uniforms: uniforms,
            samplers: samplers,
            defines: defines
        });
        this.update_bake_material(bake_material, lights);
        bake_material.sideOrientation = BABYLON_Material.CounterClockWiseSideOrientation;
        bake_material.backFaceCulling = false;
        return bake_material;
    }
    // Note, the new set of lights must be equal in amount to the amount of lights provided in the material's defines.
    update_bake_material(bake_material, lights) {
        const centers = [];
        const sizes = [];
        const colors = [];
        lights.forEach(l => {
            centers.push(l.center.x, l.center.y, l.center.z);
            sizes.push(l.size.x, l.size.y);
            if (l.color !== undefined) {
                colors.push(l.color.r, l.color.g, l.color.b);
            }
            else {
                colors.push(1.0, 1.0, 1.0);
            }
        });
        bake_material.setArray3('light_centers', centers);
        bake_material.setArray3('light_colors', colors);
        bake_material.setArray2('light_sizes', sizes);
        bake_material.setFloats('light_roty', lights.map(l => l.roty));
        bake_material.setFloats('light_rotz', lights.map(l => l.rotz));
        bake_material.setFloats('light_intensities', lights.map(l => l.intensity));
    }
    create_render_target(render_list, uvset, width, height, name) {
        const render_target = new BABYLON_RenderTargetTexture('rendertarget_' + name, { width: width, height: height }, this._scene, false, undefined, this._texture_format, undefined, BABYLON_RenderTargetTexture.LINEAR_LINEAR, false, false);
        render_target.wrapU = BABYLON_Texture.CLAMP_ADDRESSMODE;
        render_target.wrapV = BABYLON_Texture.CLAMP_ADDRESSMODE;
        render_target.anisotropicFilteringLevel = 1;
        render_target.clearColor = new BABYLON_Color4(0.5, 0.5, 0.5, 0.0);
        render_target.activeCamera = this._bake_camera;
        render_target.coordinatesIndex = uvset; // use uv2 when read as a lightmap
        render_target.renderList = render_list;
        const defines = this._float_rendering ? '' : '#define PACKEDLIGHTMAP';
        // You can add a number of extra dilation passes as follows.
        // for (let i = 0; i < 5; i++) {
        //   const post = new BABYLON_PostProcess('finalize_lightmap1', 'finalize_lightmap', ['final_frame', 'dilation_enable', 'texel_size'], null, 1, render_target.activeCamera, BABYLON_Texture.NEAREST_SAMPLINGMODE, undefined, undefined, defines, this.texture_format)
        //   post.onApply = (effect) => {
        //     effect.setVector2('texel_size', new BABYLON_Vector2(1.0 / width, 1.0 / height))
        //     effect.setFloat('final_frame', -1.0)
        //     effect.setFloat('dilation_enable', 1.0)
        //   }
        //   render_target.addPostProcess(post)
        // }
        {
            const post = new BABYLON_PostProcess('finalize_lightmap', 'finalize_lightmap', ['final_frame', 'dilation_enable', 'texel_size'], null, 1, render_target.activeCamera, BABYLON_Texture.NEAREST_SAMPLINGMODE, undefined, undefined, defines, this._texture_format);
            post.onApply = (effect) => {
                effect.setVector2('texel_size', new BABYLON_Vector2(1.0 / width, 1.0 / height));
                effect.setFloat('final_frame', this.last_frame() ? 1.0 : -1.0);
                effect.setFloat('dilation_enable', 1.0);
            };
            render_target.addPostProcess(post);
        }
        render_target.onBeforeRenderObservable.add(this.on_before_render.bind(this));
        render_target.onAfterRenderObservable.add(this.on_after_render.bind(this));
        return render_target;
    }
    on_before_render() {
        this._restore_material_map.clear();
        if (this._write_rt.renderList) {
            let material_ready = true;
            this._write_rt.renderList.forEach(mesh => {
                if (mesh instanceof BABYLON_InstancedMesh) {
                    this._restore_material_map.set(mesh, mesh.sourceMesh.material);
                    mesh.sourceMesh.material = this._bake_material;
                }
                else {
                    this._restore_material_map.set(mesh, mesh.material);
                    mesh.material = this._bake_material;
                }
                material_ready = material_ready && this._bake_material.isReady(mesh);
            });
            this._material_ready = this._material_ready || material_ready;
        }
        if (this._material_ready) {
            this._sample_nr += this._concurrent_samples;
        }
    }
    on_after_render() {
        this._restore_material_map.forEach((material, mesh) => {
            if (mesh instanceof BABYLON_InstancedMesh) {
                mesh.sourceMesh.material = material;
            }
            else {
                mesh.material = material;
            }
        });
        if (this._material_ready) {
            // If baking is done, remove bake functions from render loop
            if (this._sample_nr >= this._total_samples) {
                this.stop_bake();
            }
            this.swap_rt();
        }
    }
    swap_rt() {
        const temp_rt = this._write_rt;
        this._write_rt = this._read_rt;
        this._read_rt = temp_rt;
    }
    stop_bake() {
        this.running = false;
        if (this._main_render_observer !== null) {
            this._viewer.get_before_render_scene_setup_observable().remove(this._main_render_observer);
            this._main_render_observer = null;
        }
        const rt_with_result = this._total_samples % 2 === 0 ? this._read_rt : this._write_rt;
        const the_other_rt = this._total_samples % 2 === 0 ? this._write_rt : this._read_rt;
        the_other_rt.dispose(); // Dispose the render target we don't return.
        if (this._on_stop !== null) {
            this._on_stop(rt_with_result);
        }
        this._viewer.mark_scene_dirty();
    }
    get_visible_babylon_meshes(object_ids) {
        const meshes = [];
        const objects = this._viewer.viewer_scene.get_objects(object_ids);
        objects.forEach(object => {
            object.mesh_iterator(true, m => {
                if (m.isVisible && m.isEnabled()) {
                    meshes.push(m);
                }
            });
        });
        return meshes;
    }
}
//# sourceMappingURL=LightBaker.js.map